import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const UIContext = createContext(null);

/**
 * Minimal app-wide UI services: toasts + confirm dialog + prompt
 * dialog. Lightweight port of noteshq-web's UIProvider — dropped
 * in here rather than imported because the admin tree is
 * intentionally self-contained (different deploy target, different
 * auth domain, no shared build).
 *
 *   const { toast, confirm, prompt } = useUI();
 *   toast.success('Saved.');
 *   if (await confirm({ title: 'Ban user?', destructive: true })) ...
 *   const reason = await prompt({ title: 'Reason?', required: true });
 *   if (reason == null) return; // user cancelled
 *
 * Returns null (not '') when the user cancels so the caller can
 * distinguish "they hit Cancel" from "they confirmed an empty
 * string". Pass `required: true` to reject an empty submission.
 *
 * @author <lukman@zshift.io>
 */
export function UIProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmReq, setConfirmReq] = useState(null);
  const [promptReq,  setPromptReq]  = useState(null);

  // Auto-dismiss after 4s. The timeouts live in a ref so they can be
  // cleared if the user dismisses earlier; otherwise we'd leak them.
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((arr) => arr.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) { clearTimeout(handle); timers.current.delete(id); }
  }, []);

  const push = useCallback((kind, message) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((arr) => [...arr, { id, kind, message }]);
    const handle = setTimeout(() => dismiss(id), 4000);
    timers.current.set(id, handle);
  }, [dismiss]);

  useEffect(() => () => { for (const h of timers.current.values()) clearTimeout(h); }, []);

  const toast = {
    success: (m) => push('success', m),
    error:   (m) => push('error',   m),
    info:    (m) => push('info',    m),
  };

  // Promise-based confirm. The component renders <ConfirmDialog>
  // when confirmReq is set; the dialog's button handlers resolve
  // the stashed Promise.
  const confirm = useCallback((opts) => new Promise((resolve) => {
    setConfirmReq({ ...opts, resolve });
  }), []);

  const onConfirm = () => { confirmReq?.resolve(true); setConfirmReq(null); };
  const onCancel  = () => { confirmReq?.resolve(false); setConfirmReq(null); };

  // Prompt — same pattern as confirm but the resolve value is the
  // string the user typed (or null if they cancelled).
  const prompt = useCallback((opts) => new Promise((resolve) => {
    setPromptReq({ ...opts, resolve });
  }), []);

  const onPromptSubmit = (value) => { promptReq?.resolve(value); setPromptReq(null); };
  const onPromptCancel = () => { promptReq?.resolve(null); setPromptReq(null); };

  return (
    <UIContext.Provider value={{ toast, confirm, prompt }}>
      {children}
      <ToastStack toasts={toasts} dismiss={dismiss} />
      {confirmReq && (
        <ConfirmDialog req={confirmReq} onConfirm={onConfirm} onCancel={onCancel} />
      )}
      {promptReq && (
        <PromptDialog req={promptReq} onSubmit={onPromptSubmit} onCancel={onPromptCancel} />
      )}
    </UIContext.Provider>
  );
}

export function useUI() {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used inside UIProvider');
  return ctx;
}

// ── Toast stack ─────────────────────────────────────────────────
function ToastStack({ toasts, dismiss }) {
  return (
    <div className="toast-stack" aria-live="polite" aria-atomic="true">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.kind}`} onClick={() => dismiss(t.id)}>
          <span className="toast-msg">{t.message}</span>
          <button className="toast-x" aria-label="Dismiss">×</button>
        </div>
      ))}
    </div>
  );
}

// ── Confirm dialog ──────────────────────────────────────────────
function ConfirmDialog({ req, onConfirm, onCancel }) {
  // Close on Escape — matches what users expect from a modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, onConfirm]);

  return (
    <div className="confirm-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="confirm-card" role="dialog" aria-modal="true">
        {req.title && <h3 className="confirm-title">{req.title}</h3>}
        {req.message && <p className="confirm-msg">{req.message}</p>}
        <div className="confirm-actions">
          <button className="btn ghost" onClick={onCancel}>
            {req.cancelLabel || 'Cancel'}
          </button>
          <button
            className={`btn ${req.destructive ? 'danger' : 'primary'}`}
            onClick={onConfirm}
            autoFocus
          >
            {req.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Prompt dialog ───────────────────────────────────────────────
// Replaces native window.prompt() with a styled modal — supports
// single-line input or multiline textarea, character cap, required
// validation, and an optional placeholder. Like ConfirmDialog it
// closes on Esc / backdrop click (returns null), and Enter submits
// in single-line mode (Ctrl/Cmd+Enter in multiline mode so plain
// Enter can insert a newline).
function PromptDialog({ req, onSubmit, onCancel }) {
  const [value, setValue] = useState(req.defaultValue || '');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Auto-focus + select on open so the user can type immediately
  // (or start typing over a pre-filled default).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (req.defaultValue) el.select();
  }, [req.defaultValue]);

  const submit = useCallback(() => {
    const trimmed = req.trim === false ? value : value.trim();
    if (req.required && !trimmed) {
      setError(req.requiredMessage || 'This field is required.');
      return;
    }
    if (req.maxLength && trimmed.length > req.maxLength) {
      setError(`Must be ${req.maxLength} characters or fewer.`);
      return;
    }
    onSubmit(trimmed);
  }, [value, req, onSubmit]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); return; }
      // Single-line: Enter submits.
      // Multiline: Cmd/Ctrl+Enter submits so plain Enter inserts a newline.
      const multi = !!req.multiline;
      if (e.key === 'Enter') {
        if (!multi || e.metaKey || e.ctrlKey) {
          e.preventDefault();
          submit();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, submit, req.multiline]);

  const InputTag = req.multiline ? 'textarea' : 'input';

  return (
    <div className="confirm-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="confirm-card" role="dialog" aria-modal="true" style={{ maxWidth: 460 }}>
        {req.title && <h3 className="confirm-title">{req.title}</h3>}
        {req.message && <p className="confirm-msg">{req.message}</p>}

        <InputTag
          ref={inputRef}
          className={`prompt-input ${req.multiline ? 'prompt-textarea' : ''}`}
          placeholder={req.placeholder || ''}
          value={value}
          onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
          rows={req.multiline ? (req.rows || 3) : undefined}
          type={!req.multiline ? (req.type || 'text') : undefined}
          maxLength={req.maxLength}
          aria-invalid={!!error}
        />

        {error && <div className="prompt-error">{error}</div>}

        {req.maxLength && !error && (
          <div className="prompt-counter">
            {value.length} / {req.maxLength}
          </div>
        )}

        <div className="confirm-actions">
          <button className="btn ghost" onClick={onCancel}>
            {req.cancelLabel || 'Cancel'}
          </button>
          <button
            className={`btn ${req.destructive ? 'danger' : 'primary'}`}
            onClick={submit}
          >
            {req.confirmLabel || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}
